import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../services/api';

interface AuthContextType {
    authenticated: boolean;
    accessToken: string | null;
    user: any | null;
    isInitializing: boolean;
    signIn: (credentials: { email: string; password: string }) => Promise<any>;
    signOut: () => Promise<void>;
    signUp: (user: { name: string; email: string; password: string }) => Promise<any>;
    check: () => Promise<boolean>;
    syncProfile: () => Promise<void>;
    handleToken: (token: string) => Promise<any>;
    changePassword: (data: { oldPassword: string; newPassword: string }) => Promise<any>;
    updateProfile: (data: any) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
    if (!token || token === '') {
        return true;
    }
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp;
        const now = Math.floor(Date.now() / 1000);
        return exp < now;
    } catch {
        return true;
    }
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [user, setUser] = useState<any | null>(null);
    const [isInitializing, setIsInitializing] = useState<boolean>(true);

    // Initialize from localStorage and verify token
    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('accessToken');
            const storedUser = localStorage.getItem('userDetail');

            if (token && !isTokenExpired(token)) {
                setAccessToken(token);
                setAuthenticated(true);
                if (storedUser) {
                    try {
                        setUser(JSON.parse(storedUser));
                    } catch (e) {
                        console.error('Error parsing stored user:', e);
                    }
                }
                /* Background sync removed - handled by syncProfile or explicit fetch */
            } else {
                setAccessToken(null);
                setAuthenticated(false);
                setUser(null);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('userDetail');
                localStorage.removeItem('userType');
            }
            setIsInitializing(false);
        };
        initializeAuth();
    }, []);

    const updateToken = useCallback((token: string | null) => {
        setAccessToken(token);
        setAuthenticated(!!token);
        if (token) {
            localStorage.setItem('accessToken', token);
        } else {
            localStorage.removeItem('accessToken');
        }
    }, []);

    const signIn = useCallback(async (credentials: { email: string; password: string }): Promise<any> => {
        const response = await api.post('auth/login', credentials);

        // Try multiple possible locations for the token
        let token: string | null = null;
        const data = response.data;

        if (data?.data?.tokens?.accessToken) {
            token = data.data.tokens.accessToken;
        } else if (data?.data?.token) {
            token = data.data.token;
        } else if (data?.data?.accessToken) {
            token = data.data.accessToken;
        } else if (data?.token) {
            token = data.token;
        } else if (data?.accessToken) {
            token = data.accessToken;
        }

        if (token) {
            updateToken(token);
        } else {
            console.warn('Token not found in response. Response structure:', data);
        }

        // Store admin details
        if (data?.data?.admin) {
            const adminData = data.data.admin;
            const normalizedAdmin = {
                ...adminData,
                menu_permission: adminData.menuPermissions || adminData.menuPermission || adminData.menu_permission || []
            };
            if (normalizedAdmin.menuPermissions) {
                delete normalizedAdmin.menuPermissions;
            }
            localStorage.setItem('userDetail', JSON.stringify(normalizedAdmin));
            localStorage.setItem('userType', 'admin');
            setUser(normalizedAdmin);
        }

        return data;
    }, [updateToken]);

    const signOut = useCallback(async (): Promise<void> => {
        try {
            // Optional: call logout API
            await api.post('auth/logout').catch(() => { });
        } catch (error) {
            // Silently fail
        }
        updateToken(null);
        setUser(null);
        localStorage.removeItem('userDetail');
        localStorage.removeItem('userType');
        localStorage.removeItem('accessToken'); // Just to be safe
    }, [updateToken]);

    const signUp = useCallback(async (user: { name: string; email: string; password: string }): Promise<any> => {
        const response = await api.post(`auth/register`, user);
        return response.data;
    }, []);

    const check = useCallback(async (): Promise<boolean> => {
        const token = accessToken || localStorage.getItem('accessToken');

        if (token && !isTokenExpired(token)) {
            if (!accessToken) {
                setAccessToken(token);
            }
            setAuthenticated(true);
            return true;
        }

        if (authenticated) {
            signOut();
        }
        return false;
    }, [accessToken, authenticated, signOut]);

    const syncProfile = useCallback(async (): Promise<void> => {
        if (!authenticated && !accessToken) return;

        try {
            const response = await api.get('auth/profile');
            if (response.data?.success && response.data?.data?.admin) {
                const adminData = response.data.data.admin;
                const normalizedAdmin = {
                    ...adminData,
                    menu_permission: adminData.menuPermissions || adminData.menuPermission || adminData.menu_permission || []
                };
                if (normalizedAdmin.menuPermissions) delete (normalizedAdmin as any).menuPermissions;

                // Only update state if data has changed to prevent re-renders
                const currentStored = localStorage.getItem('userDetail');
                if (currentStored !== JSON.stringify(normalizedAdmin)) {
                    localStorage.setItem('userDetail', JSON.stringify(normalizedAdmin));
                    setUser(normalizedAdmin);
                }
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                signOut();
            }
        }
    }, [authenticated, accessToken, signOut]);

    const handleToken = useCallback(async (token: string): Promise<any> => {
        const response = await api.post(`auth/verify-token`, { token });
        const data = response.data;

        if (data?.data?.token) {
            updateToken(data.data.token);
        }

        if (data?.data?.user) {
            const user = data.data.user;
            localStorage.setItem('userDetail', JSON.stringify(user));
            localStorage.setItem('userType', 'user');
        }

        return data;
    }, [updateToken]);

    const changePassword = useCallback(async (data: { oldPassword: string; newPassword: string }): Promise<any> => {
        const response = await api.put(`auth/change-password`, data);
        return response.data;
    }, []);

    const updateProfile = useCallback(async (data: any): Promise<any> => {
        const response = await api.put(`auth/profile`, data);
        const responseData = response.data;

        if (responseData?.data?.admin) {
            const admin = responseData.data.admin;
            // Normalize menuPermissions (camelCase) to menu_permission (snake_case)
            const normalizedAdmin = {
                ...admin,
                menu_permission: admin.menuPermissions || admin.menuPermission || admin.menu_permission || []
            };
            // Remove menuPermissions if it exists to avoid confusion
            if (normalizedAdmin.menuPermissions) {
                delete normalizedAdmin.menuPermissions;
            }
            localStorage.setItem('userDetail', JSON.stringify(normalizedAdmin));
            setUser(normalizedAdmin);
        }

        return responseData;
    }, []);

    return (
        <AuthContext.Provider
            value={{
                authenticated,
                accessToken,
                user,
                isInitializing,
                signIn,
                signOut,
                signUp,
                check,
                syncProfile,
                handleToken,
                changePassword,
                updateProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
